import {JsonObject} from "../common/json/json-object";
import {JsonProperty} from "../common/json/json-property";
import {BaseListItem, BaseListItemsLoader, UserDisplayName} from "../shared/types";
import {JSON_OBJECT} from "../shared/json/helpers";
import {createGuestMemberAuth, getMemberAuth} from "../shared/auth";
import {Member, Members} from "../shared/entities";
import {WEEK_IN_MILLIS} from "../shared/date_util";
import {FormGen} from "../shared/formgen";

export const ROOM_CODE_LENGTH = 8; // Must be an even number.

@JsonObject()
export class Settings {

  @JsonProperty()
  @FormGen({name: "Room photo", type: "profile_photo"})
  readonly roomPhoto: string;

  @JsonProperty()
  @FormGen({name: "Room name", type: "string"})
  readonly roomName: string;

  constructor(roomName: string = "My room") {
    this.roomName = roomName;
  }

  clone(): Settings {
    return JSON_OBJECT.deserializeObject(JSON_OBJECT.serializeObject(this), Settings);
  }
}

@JsonObject()
export class Room extends BaseListItem {

  joiner: Member;

  @JsonProperty()
  creatorNickname: string;

  @JsonProperty()
  joinedBy: string;

  @JsonProperty()
  joinerNickname: string;

  @JsonProperty()
  joinedAt: number;

  @JsonProperty()
  updatedAt: number;

  @JsonProperty()
  archivedAt: number;

  @JsonProperty()
  deletedAt: number;

  @JsonProperty()
  settings: Settings = new Settings();

  constructor(id: string, creator: string, created: number, creatorNickname: string) {
    super(id, creator, created);
    this.creatorNickname = creatorNickname;
  }

  updateSelf(settings?: Settings): Room {
    if (settings) {
      this.settings = settings;
    }
    return this;
  }

  async onAfterItemDeserialized(): Promise<void> {
    if (!this.creatorNickname) {
      const creatorMember = await Members.getInstance().getOrLoadMember(this.creator)
      this.creatorNickname = creatorMember ? UserDisplayName(creatorMember.user) : "Guest User";
    }
    if (this.joinedBy) {
      let joiner = await Members.getInstance().getOrLoadMember(this.joinedBy);
      if (!joiner) {
        joiner = createGuestMemberAuth(this.joinedBy).member;
      }
      this.joiner = joiner;
    }
  }

  isArchived(): boolean {
    return this.archivedAt > 0;
  }

  isDeleted(): boolean {
    return this.deletedAt > 0;
  }

  isUpdatable(): boolean {
    return (Date.now() < (Math.max(this.created, this.updatedAt || 0)) + 12 * WEEK_IN_MILLIS) && Boolean(this.joinedAt) && Boolean(this.joinedBy) && !this.isArchived() && !this.isDeleted();
  }
}

export class Rooms extends BaseListItemsLoader<Room> {

  private static instance: Rooms;

  static getInstance(): Rooms {
    if (!this.instance) {
      this.instance = new Rooms();
    }
    return this.instance;
  }

  constructor() {
    super({shared: true});
  }

  protected basePath(): string {
    return "rooms";
  }

  async loadListItems(): Promise<void> {
    throw new Error("Not supported. We should never try to list all rooms.")
  }

  protected deserializeItem(value: any): Room {
    return JSON_OBJECT.deserializeObject(value, Room);
  }

  protected serializeItem(item: Room): any {
    return JSON_OBJECT.serializeObject(item);
  }

  protected sortOrder(item1: Room, item2: Room): number {
    return 0;
  }
}

@JsonObject()
export class RoomJoin extends BaseListItem {

  static createNew(roomId: string): RoomJoin {
    return new RoomJoin(roomId, getMemberAuth().getMemberId(), Date.now());
  }

  constructor(id: string, creator: string, created: number) {
    super(id, creator, created);
  }
}

export class RoomJoins extends BaseListItemsLoader<RoomJoin> {

  private static instance: RoomJoins;

  static getInstance(): RoomJoins {
    if (!this.instance) {
      this.instance = new RoomJoins();
    }
    return this.instance;
  }

  protected basePath(): string {
    return "room_joins";
  }

  protected deserializeItem(value: any): RoomJoin {
    return JSON_OBJECT.deserializeObject(value, RoomJoin);
  }

  protected serializeItem(item: RoomJoin): any {
    return JSON_OBJECT.serializeObject(item);
  }

  protected sortOrder(item1: RoomJoin, item2: RoomJoin): number {
    return item2.created - item1.created;
  }
}

@JsonObject()
export class RoomChat extends BaseListItem {

  static createNew(roomId: string): RoomChat {
    return new RoomChat(roomId, getMemberAuth().getMemberId(), Date.now());
  }

  constructor(id: string, creator: string, created: number) {
    super(id, creator, created);
  }
}

export class RoomChats extends BaseListItemsLoader<RoomChat> {

  private static instance: RoomChats;

  static getInstance(): RoomChats {
    if (!this.instance) {
      this.instance = new RoomChats();
    }
    return this.instance;
  }

  constructor() {
    super({shared: true});
  }

  protected basePath(): string {
    return "room_chats";
  }

  protected deserializeItem(value: any): RoomChat {
    return JSON_OBJECT.deserializeObject(value, RoomChat);
  }

  protected serializeItem(item: RoomChat): any {
    return JSON_OBJECT.serializeObject(item);
  }

  protected sortOrder(item1: RoomChat, item2: RoomChat): number {
    return item2.created - item1.created;
  }
}